import type {
  AuthUser,
  BillingRedirect,
  PlanInfo,
  ProviderBilling,
  CreatedUpload,
  Paginated,
  ProposalInput,
  ProviderCompanyForm,
  ProviderDetailsForm,
  ProviderDocumentInfo,
  ProviderDocumentInput,
  ProviderJob,
  ProviderJobListItem,
  ProviderJobListQuery,
  ProviderOverview,
  ProviderProfile,
  ProviderProposal,
  ProviderProposalListQuery,
  ProviderServicesInput,
  ProviderSignupForm,
  ProviderStats,
  PublicProviderProfile,
  UploadInfo,
} from '@hemmafixa/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { sessionKey } from '@/features/auth/api';
import { ApiError } from '@/lib/api';
import { api } from '@/lib/api-client';
import { recaptchaToken } from '@/lib/recaptcha';

// The company profile, onboarding (P2-09), the public profile (P2-11), jobs and proposals (P4-06) are real.
// Plans, the subscription and credits are real (P6-02, P6-04).

const profileKey = ['provider', 'profile'] as const;

/** The signed-in provider's company profile; null when there is none yet. */
export function useProviderProfile() {
  return useQuery({
    queryKey: profileKey,
    queryFn: async () => {
      try {
        return await api<ProviderProfile>('/provider/profile');
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
      }
    },
  });
}

function useProfileChange<TInput>(request: (input: TInput) => Promise<ProviderProfile>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: request,
    onSuccess: (profile) => queryClient.setQueryData(profileKey, profile),
  });
}

export const useSaveCompany = () =>
  useProfileChange<ProviderCompanyForm>((input) =>
    api<ProviderProfile>('/provider/profile/company', { method: 'PUT', body: input }),
  );

export const useSaveServices = () =>
  useProfileChange<ProviderServicesInput>((input) =>
    api<ProviderProfile>('/provider/profile/services', { method: 'PUT', body: input }),
  );

export const useSaveDetails = () =>
  useProfileChange<ProviderDetailsForm>((input) =>
    api<ProviderProfile>('/provider/profile/details', { method: 'PUT', body: input }),
  );

/** A verified provider as clients see them (P2-11); null for an unknown or unverified one. */
export function usePublicProvider(id: string | undefined) {
  return useQuery({
    queryKey: ['providers', 'public', id],
    queryFn: async () => {
      try {
        return await api<PublicProviderProfile>(`/providers/${id}`);
      } catch (error) {
        if (error instanceof ApiError && (error.status === 404 || error.status === 400)) return null;
        throw error;
      }
    },
    enabled: Boolean(id),
  });
}

export const useSubmitProfile = () =>
  useProfileChange<void>(() => api<ProviderProfile>('/provider/profile/submit', { method: 'POST' }));

/** Public sign-up for someone without an account. The API answers the same for known and new emails. */
export function useProviderSignup() {
  return useMutation({
    mutationFn: async (input: Omit<ProviderSignupForm, 'recaptchaToken'>) =>
      api<void>('/provider-signups', {
        method: 'POST',
        body: { ...input, recaptchaToken: await recaptchaToken('provider_signup') },
      }),
  });
}

/** A signed-in user becomes a provider; the session gets the new role so the provider area opens at once. */
export function useStartProvider() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api<AuthUser>('/provider/start', { method: 'POST' }),
    onSuccess: (user) => queryClient.setQueryData(sessionKey, user),
  });
}
const jobsKey = ['provider', 'jobs'] as const;
const proposalsKey = ['provider', 'proposals'] as const;

function queryString(params: Record<string, string | number | boolean | undefined>) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : '';
}

type OpenJobsParams = Partial<
  Pick<ProviderJobListQuery, 'q' | 'category' | 'wantedStart' | 'proposal' | 'saved' | 'page' | 'pageSize'>
>;

/** Matching open jobs to quote on (P4-06), with filters and saved jobs (P4-03). */
export function useOpenJobs(params: OpenJobsParams = {}) {
  return useQuery({
    queryKey: [...jobsKey, 'list', params],
    queryFn: () => api<Paginated<ProviderJobListItem>>(`/provider/jobs${queryString(params)}`),
    placeholderData: keepPreviousData,
  });
}

/** One job with the provider's own proposal; null for a closed job they never quoted on, or a wrong link. */
export function useOpenJob(id: string) {
  return useQuery({
    queryKey: [...jobsKey, 'job', id],
    queryFn: async () => {
      try {
        return await api<ProviderJob>(`/provider/jobs/${id}`);
      } catch (error) {
        if (error instanceof ApiError && (error.status === 404 || error.status === 400)) return null;
        throw error;
      }
    },
  });
}

/** Saves or unsaves a job; lists and job pages show the new state after a refresh. */
export function useSaveJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, saved }: { id: string; saved: boolean }) =>
      api<void>(`/provider/jobs/${id}/saved`, { method: saved ? 'PUT' : 'DELETE' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: jobsKey }),
  });
}

type MyProposalsParams = Partial<Pick<ProviderProposalListQuery, 'filter' | 'status' | 'page' | 'pageSize'>>;

export function useMyProposals(params: MyProposalsParams = {}) {
  return useQuery({
    queryKey: [...proposalsKey, 'list', params],
    queryFn: () => api<Paginated<ProviderProposal>>(`/provider/proposals${queryString(params)}`),
    placeholderData: keepPreviousData,
  });
}

/** Sending, changing or withdrawing a proposal refreshes the job page and both lists. */
function useProposalChange<TInput>(request: (input: TInput) => Promise<ProviderProposal>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: request,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: jobsKey });
      void queryClient.invalidateQueries({ queryKey: proposalsKey });
    },
  });
}

export const useSendProposal = (jobId: string) =>
  useProposalChange<ProposalInput>((input) =>
    api<ProviderProposal>(`/jobs/${jobId}/proposals`, { method: 'POST', body: input }),
  );

export const useEditProposal = (id: string) =>
  useProposalChange<ProposalInput>((input) =>
    api<ProviderProposal>(`/provider/proposals/${id}`, { method: 'PATCH', body: input }),
  );

export const useWithdrawProposal = (id: string) =>
  useProposalChange<void>(() =>
    api<ProviderProposal>(`/provider/proposals/${id}/withdraw`, { method: 'POST' }),
  );

const documentsKey = ['provider', 'documents'] as const;

/** The company's documents with 5-minute links */
export function useProviderDocuments() {
  return useQuery({
    queryKey: documentsKey,
    queryFn: () => api<ProviderDocumentInfo[]>('/provider/profile/documents'),
    // The links last 5 minutes; refresh before they run out while the page stays open
    refetchInterval: 4 * 60_000,
  });
}

export function useProviderDocumentActions() {
  const queryClient = useQueryClient();
  return useMemo(
    () => ({
      create: (input: ProviderDocumentInput) =>
        api<CreatedUpload>('/provider/profile/documents', { method: 'POST', body: input }),
      complete: (uploadId: string) =>
        api<UploadInfo>(`/provider/profile/documents/${uploadId}/complete`, { method: 'POST' }),
      remove: (uploadId: string) =>
        api<void>(`/provider/profile/documents/${uploadId}`, { method: 'DELETE' }),
      refresh: () => void queryClient.invalidateQueries({ queryKey: documentsKey }),
    }),
    [queryClient],
  );
}

/** Start-page numbers (P5-07); refreshed every minute while the page is open */
export function useProviderOverview() {
  return useQuery({
    queryKey: ['provider', 'overview'],
    queryFn: () => api<ProviderOverview>('/provider/overview'),
    refetchInterval: 60_000,
  });
}

/** Start-page charts (UI-05): the last six months and totals by status */
export function useProviderCharts() {
  return useQuery({
    queryKey: ['provider', 'charts'],
    queryFn: () => api<ProviderStats>('/provider/stats'),
    staleTime: 5 * 60_000,
  });
}

/** Active plans for the billing page */
export function usePlans() {
  return useQuery({ queryKey: ['plans'], queryFn: () => api<PlanInfo[]>('/plans'), staleTime: 5 * 60_000 });
}

/** The subscription and invoices */
export function useProviderBilling() {
  return useQuery({
    queryKey: ['provider', 'billing'],
    queryFn: () => api<ProviderBilling>('/provider/billing'),
  });
}

// Both return a Stripe page; the browser leaves the app for it
export const useStartCheckout = () =>
  useMutation({
    mutationFn: (planId: string) =>
      api<BillingRedirect>('/provider/billing/checkout', { method: 'POST', body: { planId } }),
    onSuccess: ({ url }) => window.location.assign(url),
  });

export const useBuyCreditPack = () =>
  useMutation({
    mutationFn: () => api<BillingRedirect>('/provider/billing/credit-pack', { method: 'POST' }),
    onSuccess: ({ url }) => window.location.assign(url),
  });

export const useOpenBillingPortal = () =>
  useMutation({
    mutationFn: () => api<BillingRedirect>('/provider/billing/portal', { method: 'POST' }),
    onSuccess: ({ url }) => window.location.assign(url),
  });
